import type { ContentHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import type { BatchItem } from '@generalTypes/sriTypes';
import {
  selectApiWithPendingChanges,
  selectWebPage,
} from '@newStore/documentApi/documentApiSelectors';
import { wrapNodeTypeLabel } from '@newStore/documentUI/transformProposal/proposalHelpers';
import { createTypedSelector } from '@newStore/genericHelpers';
import type {
  AsyncValidationRule,
  ContentItemsWithWebPage,
  ValidationResult,
  ValidationRuleName,
} from '@newStore/validation/validationTypes';
import { getNodeTypeConfig } from '@nodeTypeConfig/index';
import { getNodeType } from '@nodeTypeConfig/nodeTypeConfig';
import { isEqual, pick } from 'lodash';
import type { EditComponent } from '../../../config/nodeTypeConfigurations/configTypes';
import { createError } from '../createError';
import { VALIDATION_PENDING } from '../validationHelpers';

const getWebPageFromApiResponse = (response: ContentItemsWithWebPage[0]) => {
  const [webPage] = response.$$webPages;
  return webPage.$$expanded;
};

const selectWebPageHash: AsyncValidationRule['selectInvalidateDataOnChangeOf'] = (
  state,
  href: ContentHref,
) => {
  const webpage = selectWebPage(state, href);
  return `${webpage?.path}-${webpage?.oldLocations?.map((l) => l.website.href + l.path).join('-')}`;
};

const selectValidationData = (state: RootState, href: ContentHref) =>
  !state.validationData[href]?.noDuplicateWebPaths?.isLoading &&
  selectWebPageHash(state, href) ===
    state.validationData[href]?.noDuplicateWebPaths?.validityHash &&
  state.validationData[href]?.noDuplicateWebPaths?.data;

/**
 * the validation is used for both the path and the oldLocations property
 * the oldLocations property is an array of objects with a path and a website
 * the path property is a string
 * a path in oldLocations or the path property should be unique within the same website.
 */
const selectValidation: AsyncValidationRule['selectValidation'] = createTypedSelector(
  [
    (state, href: ContentHref) => selectWebPage(state, href),
    (state, href: ContentHref) => selectValidationData(state, href),
    (
      state,
      href: ContentHref,
      parent: ContentHref | undefined,
      config: EditComponent | undefined,
    ) => config,
  ],
  (webPage, validationData, config): ValidationResult => {
    if (!webPage || webPage.$$meta.deleted) {
      return true;
    }

    if (!config) {
      console.error('referencedInProHomeCannotUnpublish: config is undefined');
      return true;
    }

    if (config.property !== 'path' && config.property !== 'oldLocations') {
      throw new Error(
        'noDuplicateWebPaths should only be used on the path and oldLocations property',
      );
    }

    if (!validationData) {
      return VALIDATION_PENDING;
    }

    const duplicate =
      config.property === 'path'
        ? validationData.find((cont) => {
            const web = getWebPageFromApiResponse(cont);
            return (
              (web.website.href === webPage.website.href && web.path === webPage.path) ||
              web.oldLocations?.some(
                (l) => l.website.href === webPage.website.href && l.path === webPage.path,
              )
            );
          })
        : validationData.find((cont) => {
            const web = getWebPageFromApiResponse(cont);
            return webPage.oldLocations?.some(
              (oldLoc) =>
                (oldLoc.website.href === web.website.href && web.path === oldLoc.path) ||
                web.oldLocations?.some(
                  (x) => x.website.href === oldLoc.website.href && x.path === oldLoc.path,
                ),
            );
          });

    if (duplicate) {
      const web = getWebPageFromApiResponse(duplicate);
      const duplicateDocType = getNodeType(duplicate, web, null);
      const nodeTypeLabel = getNodeTypeConfig(duplicateDocType).information.single.split(':')[0];

      const propName = config.property === 'path' ? 'pad' : 'vorige url';

      let duplicatePath = web.path;

      if (config.property === 'oldLocations') {
        // we need to find the duplicate path either in the duplicate.path or in the oldLocations of the duplicate.
        const oldLocation = webPage.oldLocations?.find(
          (oldLoc) =>
            (oldLoc.website.href === web.website.href && web.path === oldLoc.path) ||
            web.oldLocations?.some(
              (x) => x.website.href === oldLoc.website.href && x.path === oldLoc.path,
            ),
        );

        if (oldLocation) {
          duplicatePath = oldLocation.path;
        }
      }

      const capitalizeFirstLetter = (string: string) =>
        string.charAt(0).toUpperCase() + string.slice(1);

      const message = `${capitalizeFirstLetter(propName)} <strong>${
        duplicatePath
      }</strong> wordt al gebruikt bij ${wrapNodeTypeLabel`${nodeTypeLabel}`}: "${[
        ...duplicate.$$pathToRoot,
      ]
        .reverse()
        .join(' > ')}".<br /> Kies een ander <strong>${propName}</strong>`;

      const error = createError(
        'duplicateWebPath',
        'noDuplicateWebPaths',
        message,
        pick(config, ['component', 'property']),
      );

      return error;
    }

    return true;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const noDuplicateWebPaths: AsyncValidationRule = {
  getName: (): ValidationRuleName => 'noDuplicateWebPaths',
  isAsync: true,
  selectNeedsData: (state, href) => {
    return selectValidationData(state, href) === undefined;
  },
  selectInvalidateDataOnChangeOf: selectWebPageHash,
  /**
   * Because we reuse this validationrule for both the path and the oldLocations property, the batch has each query twice.
   * this is mainly a cosmetic issue, it does not really affect performance or this validation rule.
   */
  selectDataBatch: createTypedSelector(
    [(state) => selectApiWithPendingChanges(state)?.webPages, (state, href) => href],
    (webPages, href) => {
      const webPagesForNode = webPages.filter((w) => w.source.href === href);
      const paths = webPagesForNode
        .reduce((accPaths, w) => {
          const oldLocations = w.oldLocations || [];
          return [...accPaths, w.path, ...oldLocations.map((l) => l.path)];
        }, [] as string[])
        .map((p) => p.toLowerCase())
        .join(',');

      if (!paths.length) {
        return null;
      }
      const sourceKey = href.split('/').pop();

      const batchItem: BatchItem<'GET'> = {
        verb: 'GET',
        href: `/content?limit=5000&webPageAnyPath=${paths}&$$expandPathToRoot=true&$$includeWebPages=true&keyNot=${sourceKey}`,
      };
      return batchItem;
    },
    // {
    //   memoizeOptions: {
    //     resultEqualityCheck: isEqual,
    //   },
    // }
  ),
  selectValidation,
};
